<template>
  <div class="campaign-card" :class="{ 'campaign-draft': campaign.Status === 'Draft' }">
    <!-- Card Header -->
    <div class="card-header-section">
      <div class="campaign-type-icon" :class="getTypeClass">
        <i :class="getTypeIcon"></i>
      </div>
      <div class="campaign-status">
        <b-badge :variant="getStatusVariant" pill class="status-badge">
          <i :class="getStatusIcon" class="mr-1"></i>
          {{ campaign.Status }}
        </b-badge>
      </div>
      <b-dropdown 
        variant="link" 
        no-caret 
        right 
        toggle-class="text-decoration-none card-menu-toggle"
        @click.stop
      >
        <template #button-content>
          <i class="fa fa-ellipsis-h"></i>
        </template>
        <b-dropdown-item @click.stop="$emit('view', campaign)">
          <i class="fa fa-eye mr-2 text-info"></i> View Details
        </b-dropdown-item>
        <b-dropdown-item @click.stop="$emit('edit', campaign)" v-if="campaign.Status === 'Draft'">
          <i class="fa fa-edit mr-2 text-primary"></i> Edit
        </b-dropdown-item>
        <b-dropdown-item @click.stop="$emit('duplicate', campaign)">
          <i class="fa fa-copy mr-2 text-secondary"></i> Duplicate
        </b-dropdown-item>
        <b-dropdown-item @click.stop="$emit('send', campaign)" v-if="campaign.Status === 'Draft'">
          <i class="fa fa-paper-plane mr-2 text-success"></i> Send Now
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item @click.stop="$emit('delete', campaign)" variant="danger">
          <i class="fa fa-trash mr-2"></i> Delete
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <!-- Card Body -->
    <div class="card-body-section" @click="$emit('view', campaign)">
      <h5 class="campaign-name">{{ campaign.Name }}</h5>
      <p class="campaign-description" v-if="campaign.Description">
        {{ truncateText(campaign.Description, 80) }}
      </p>

      <!-- Quick Stats -->
      <div class="quick-stats" v-if="campaign.Status !== 'Draft'">
        <div class="stat-item">
          <div class="stat-value">{{ campaign.RecipientCount | formatNumber }}</div>
          <div class="stat-label">Recipients</div>
        </div>
        <div class="stat-item">
          <div class="stat-value text-success">{{ campaign.SentCount | formatNumber }}</div>
          <div class="stat-label">Sent</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ deliveryRate }}%</div>
          <div class="stat-label">Delivered</div>
        </div>
      </div>

      <!-- Progress Bar for Active Campaigns -->
      <div class="campaign-progress" v-if="campaign.Status === 'Active' && campaign.RecipientCount > 0">
        <div class="progress-info">
          <span>Progress</span>
          <span class="progress-percent">{{ progressPercent }}%</span>
        </div>
        <b-progress :value="progressPercent" :max="100" variant="success" class="progress-bar-slim" />
      </div>

      <!-- Draft placeholder -->
      <div class="draft-placeholder" v-if="campaign.Status === 'Draft'">
        <i class="fa fa-edit"></i>
        <span>Draft - Not yet sent</span>
      </div>
    </div>

    <!-- Card Footer -->
    <div class="card-footer-section">
      <div class="footer-info">
        <span class="created-date">
          <i class="fa fa-calendar-o"></i>
          {{ formatDate(campaign.CreatedAt) }}
        </span>
        <span class="created-by" v-if="campaign.CreatedBy">
          <i class="fa fa-user"></i>
          {{ campaign.CreatedBy }}
        </span>
      </div>
      <b-button 
        size="sm" 
        :variant="campaign.Status === 'Draft' ? 'success' : 'outline-primary'"
        class="action-btn"
        @click.stop="handlePrimaryAction"
      >
        <i :class="primaryActionIcon" class="mr-1"></i>
        {{ primaryActionText }}
      </b-button>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';

export default {
  name: 'CampaignCard',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },

  computed: {
    getTypeClass() {
      const classes = {
        'SMS': 'type-sms',
        'Email': 'type-email',
        'Both': 'type-both'
      };
      return classes[this.campaign.Type] || '';
    },

    getTypeIcon() {
      const icons = {
        'SMS': 'fa fa-comment',
        'Email': 'fa fa-envelope',
        'Both': 'fa fa-comments'
      };
      return icons[this.campaign.Type] || 'fa fa-paper-plane';
    },

    getStatusVariant() {
      const variants = {
        'Draft': 'secondary',
        'Scheduled': 'warning',
        'Active': 'success',
        'Completed': 'info',
        'Paused': 'dark'
      };
      return variants[this.campaign.Status] || 'secondary';
    },

    getStatusIcon() {
      const icons = {
        'Draft': 'fa fa-edit',
        'Scheduled': 'fa fa-clock-o',
        'Active': 'fa fa-play',
        'Completed': 'fa fa-check',
        'Paused': 'fa fa-pause'
      };
      return icons[this.campaign.Status] || 'fa fa-circle';
    },

    deliveryRate() {
      if (!this.campaign.SentCount) return 0;
      return ((this.campaign.DeliveredCount / this.campaign.SentCount) * 100).toFixed(1);
    },

    progressPercent() {
      if (!this.campaign.RecipientCount) return 0;
      return Math.round((this.campaign.SentCount / this.campaign.RecipientCount) * 100);
    },

    primaryActionText() {
      if (this.campaign.Status === 'Draft') return 'Send';
      if (this.campaign.Status === 'Active') return 'View';
      return 'Details';
    },

    primaryActionIcon() {
      if (this.campaign.Status === 'Draft') return 'fa fa-paper-plane';
      return 'fa fa-arrow-right';
    }
  },

  methods: {
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('MMM D, YYYY');
    },

    handlePrimaryAction() {
      if (this.campaign.Status === 'Draft') {
        this.$emit('send', this.campaign);
      } else {
        this.$emit('view', this.campaign);
      }
    }
  },

  filters: {
    formatNumber(value) {
      if (!value) return '0';
      return value.toLocaleString();
    }
  }
};
</script>

<style lang="scss" scoped>
.campaign-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: #547fff;
  }

  &.campaign-draft {
    border-style: dashed;
    border-color: #cbd5e0;
    
    &:hover {
      border-color: #3abf94;
    }
  }

  .card-header-section {
    display: flex;
    align-items: center;
    padding: 1rem 1rem 0.5rem;
    gap: 0.75rem;

    .campaign-type-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        font-size: 1.1rem;
        color: white;
      }

      &.type-sms {
        background: linear-gradient(135deg, #3abf94 0%, #2d9474 100%);
      }
      &.type-email {
        background: linear-gradient(135deg, #547fff 0%, #3d5dff 100%);
      }
      &.type-both {
        background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
      }
    }

    .campaign-status {
      flex: 1;
      
      .status-badge {
        font-size: 0.75rem;
        padding: 4px 10px;
        font-weight: 500;
      }
    }

    ::v-deep .card-menu-toggle {
      color: #a0aec0;
      padding: 4px 8px;
      
      &:hover {
        color: #4a5568;
      }
    }
  }

  .card-body-section {
    padding: 0.5rem 1rem 1rem;
    flex: 1;
    cursor: pointer;

    .campaign-name {
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 0.5rem;
      line-height: 1.3;
    }

    .campaign-description {
      font-size: 0.85rem;
      color: #718096;
      margin-bottom: 1rem;
      line-height: 1.4;
    }

    .quick-stats {
      display: flex;
      gap: 1rem;
      padding: 0.75rem;
      background: #f8f9fa;
      border-radius: 8px;
      margin-bottom: 0.75rem;

      .stat-item {
        flex: 1;
        text-align: center;

        .stat-value {
          font-size: 1.1rem;
          font-weight: 700;
          color: #2d3748;
        }

        .stat-label {
          font-size: 0.7rem;
          color: #a0aec0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }

    .campaign-progress {
      .progress-info {
        display: flex;
        justify-content: space-between;
        font-size: 0.75rem;
        color: #718096;
        margin-bottom: 4px;

        .progress-percent {
          font-weight: 600;
          color: #3abf94;
        }
      }

      .progress-bar-slim {
        height: 6px;
        border-radius: 3px;
      }
    }

    .draft-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: #f8f9fa;
      border-radius: 8px;
      color: #a0aec0;
      font-size: 0.9rem;

      i {
        margin-right: 8px;
        font-size: 1.25rem;
      }
    }
  }

  .card-footer-section {
    padding: 0.75rem 1rem;
    border-top: 1px solid #e9ecef;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fafafa;

    .footer-info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem; /* Increased vertical spacing between date and user lines */

      .created-date,
      .created-by {
        font-size: 0.75rem;
        color: #a0aec0;
        display: flex;
        align-items: center;
        line-height: 1.4;
        margin: 0;
        padding: 0;
        
        i {
          width: 14px;
          min-width: 14px;
          flex-shrink: 0;
          text-align: center;
          font-size: 0.7rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-right: 0.5rem; /* Spacing between icon and text */
        }
      }
    }

    .action-btn {
      font-size: 0.8rem;
      padding: 0.4rem 0.75rem;
      border-radius: 6px;
      font-weight: 500;
    }
  }
}
</style>
